import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  CheckCircle2,
  Loader2,
  ArrowRight,
  Building2,
  User,
  Mail,
  Shield,
  AlertCircle,
  XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function InviteAcceptancePage() {
  const navigate = useNavigate();
  const { token } = useParams();
  const { login } = useAuth();
  
  const [isLoading, setIsLoading] = useState(true);
  const [inviteData, setInviteData] = useState(null);
  const [inviteError, setInviteError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [acceptanceComplete, setAcceptanceComplete] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Form state — no password; login is OTP-only for non-SA users
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
  });

  // Fetch invite data from backend
  useEffect(() => {
    const fetchInvite = async () => {
      setIsLoading(true);
      
      try {
        const response = await fetch(`${API_URL}/api/auth/invite/${token}`);
        const data = await response.json();
        
        if (!data.valid) {
          if (data.expired) {
            setInviteError('expired');
          } else {
            setInviteError(data.error || 'invalid');
          }
        } else {
          setInviteData({
            organizationName: data.organization_name,
            organizationId: data.organization_id,
            inviteeEmail: data.email,
            role: formatRole(data.role),
            inviterName: data.inviter_name,
            inviterEmail: data.inviter_email,
            expiresAt: data.expires_at,
          });
        }
      } catch (error) {
        console.error('Error validating invite:', error);
        setInviteError('invalid');
      }
      
      setIsLoading(false);
    };
    
    fetchInvite();
  }, [token]);

  const formatRole = (role) => {
    const roleMap = {
      'CONTRACTOR_ADMIN': 'Contractor Admin',
      'CLIENT_ADMIN': 'Contractor Admin',  // Legacy mapping
      'BID_MANAGER': 'Bid Manager',
      'REVIEWER': 'Reviewer',
      'CONSULTANT': 'Consultant',
      'SUPER_ADMIN': 'Super Admin',
    };
    return roleMap[role] || role;
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSubmitError(null);
  };

  const canSubmit = Boolean(formData.firstName && formData.lastName);

  // Generate a random password for the backend (user logs in via OTP, not password)
  const _generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    return Array.from({ length: 24 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch(`${API_URL}/api/auth/invite/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          // Backend still expects a password field — generate one silently.
          // The user will never use it; login is OTP-only for non-SA accounts.
          password: _generateRandomPassword(),
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to accept invitation');
      }
      
      // Auto-login: backend Set-Cookie already authenticated this session.
      // Cache only non-sensitive profile data for fast UI boot.
      if (data.access_token && data.user) {
        localStorage.removeItem('auth_token');
        localStorage.setItem('auth_user', JSON.stringify(data.user));

        setAcceptanceComplete(true);
      } else {
        setAcceptanceComplete(true);
      }
    } catch (error) {
      console.error('Error accepting invite:', error);
      setSubmitError(error.message);
    }
    
    setIsSubmitting(false);
  };

  const handleGoToDashboard = () => {
    // Navigate based on role
    const slug = inviteData?.organizationId?.toString().toLowerCase().replace(/\s+/g, '-') || 'workspace';
    navigate(`/${slug}/dashboard`);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-teal-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-300">Verifying your invitation...</p>
        </div>
      </div>
    );
  }

  // Error states
  if (inviteError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-slate-200 shadow-2xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              {inviteError === 'expired' ? (
                <AlertCircle className="w-8 h-8 text-red-600" />
              ) : (
                <XCircle className="w-8 h-8 text-red-600" />
              )}
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {inviteError === 'expired' ? 'Invitation Expired' : 'Invalid Invitation'}
            </h2>
            <p className="text-slate-600 mb-6">
              {inviteError === 'expired' 
                ? 'This invitation link has expired. Please contact the person who invited you to request a new invitation.'
                : inviteError === 'invalid' 
                ? 'This invitation link is invalid or has already been used. Please check the link or contact your organization administrator.'
                : inviteError}
            </p>
            <div className="space-y-3">
              <Button 
                className="w-full bg-teal-700 hover:bg-teal-800 text-white"
                onClick={() => navigate('/login')}
              >
                Go to Login
              </Button>
              <Button 
                variant="outline"
                className="w-full border-slate-200"
                onClick={() => navigate('/')}
              >
                Back to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (acceptanceComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-slate-200 shadow-2xl">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Welcome to the Team!
            </h2>
            <p className="text-slate-600 mb-6">
              Your account has been created and you've joined <strong>{inviteData.organizationName}</strong> as a {inviteData.role}.
            </p>
            <Button 
              className="w-full bg-teal-700 hover:bg-teal-800 text-white"
              onClick={handleGoToDashboard}
              data-testid="go-to-dashboard-btn"
            >
              Go to Dashboard
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-slate-200 shadow-2xl" data-testid="invite-acceptance-card">
        <CardHeader className="text-center pb-2">
          <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-6 h-6 text-teal-700" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Accept Invitation
          </CardTitle>
          <CardDescription className="text-slate-600">
            Complete your profile to join <strong>{inviteData.organizationName}</strong>
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Pre-filled Information (Read-only) */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Organization</p>
                <p className="font-semibold text-slate-900">{inviteData.organizationName}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Email</p>
                <p className="font-medium text-slate-700">{inviteData.inviteeEmail}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Your Role</p>
                <Badge className="bg-teal-100 text-teal-700 border-teal-200 mt-1">
                  {inviteData.role}
                </Badge>
              </div>
            </div>
            
            {inviteData.inviterName && (
              <div className="flex items-center gap-3 pt-2 border-t border-slate-200">
                <User className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500">Invited by</p>
                  <p className="text-sm text-slate-700">{inviteData.inviterName}</p>
                </div>
              </div>
            )}
          </div>
          
          {/* User Input Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => updateFormData('firstName', e.target.value)}
                  placeholder="John"
                  className="border-slate-200"
                  data-testid="invite-first-name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => updateFormData('lastName', e.target.value)}
                  placeholder="Smith"
                  className="border-slate-200"
                  data-testid="invite-last-name"
                  required
                />
              </div>
            </div>
            
            {submitError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                {submitError}
              </div>
            )}
            
            <Button 
              type="submit"
              className="w-full bg-teal-700 hover:bg-teal-800 text-white"
              disabled={!canSubmit || isSubmitting}
              data-testid="accept-invite-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Your Account...
                </>
              ) : (
                <>
                  Accept Invitation & Join
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>
          
          <div className="text-center pt-2">
            <p className="text-sm text-slate-500">
              Already have an account?{' '}
              <Link to="/login" className="text-teal-700 hover:underline font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
